import { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND } from '../../config/brand';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/common/Layout/Layout';
import './Account.css';

const API_URL   = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL  = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';

// Indian states list
const INDIAN_STATES = [
  { code: 'AN', name: 'Andaman and Nicobar Islands' },
  { code: 'AP', name: 'Andhra Pradesh' },
  { code: 'AR', name: 'Arunachal Pradesh' },
  { code: 'AS', name: 'Assam' },
  { code: 'BR', name: 'Bihar' },
  { code: 'CH', name: 'Chandigarh' },
  { code: 'CT', name: 'Chhattisgarh' },
  { code: 'DN', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: 'DL', name: 'Delhi' },
  { code: 'GA', name: 'Goa' },
  { code: 'GJ', name: 'Gujarat' },
  { code: 'HR', name: 'Haryana' },
  { code: 'HP', name: 'Himachal Pradesh' },
  { code: 'JK', name: 'Jammu and Kashmir' },
  { code: 'JH', name: 'Jharkhand' },
  { code: 'KA', name: 'Karnataka' },
  { code: 'KL', name: 'Kerala' },
  { code: 'LA', name: 'Ladakh' },
  { code: 'LD', name: 'Lakshadweep' },
  { code: 'MP', name: 'Madhya Pradesh' },
  { code: 'MH', name: 'Maharashtra' },
  { code: 'MN', name: 'Manipur' },
  { code: 'ML', name: 'Meghalaya' },
  { code: 'MZ', name: 'Mizoram' },
  { code: 'NL', name: 'Nagaland' },
  { code: 'OR', name: 'Odisha' },
  { code: 'PY', name: 'Puducherry' },
  { code: 'PB', name: 'Punjab' },
  { code: 'RJ', name: 'Rajasthan' },
  { code: 'SK', name: 'Sikkim' },
  { code: 'TN', name: 'Tamil Nadu' },
  { code: 'TG', name: 'Telangana' },
  { code: 'TR', name: 'Tripura' },
  { code: 'UP', name: 'Uttar Pradesh' },
  { code: 'UT', name: 'Uttarakhand' },
  { code: 'WB', name: 'West Bengal' },
];

const EMPTY_FORM = {
  label: 'Home',
  fullName: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'IN',
  isDefault: false,
};

// Pincode lookup helper
async function lookupPincode(pincode) {
  if (pincode.length !== 6) return null;
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    const data = await res.json();
    if (data?.[0]?.Status === 'Success') {
      const post = data[0].PostOffice?.[0];
      return post ? { city: post.District, state: post.State } : null;
    }
  } catch {
    // Silent fail
  }
  return null;
}

// Address Form Component
function AddressForm({ initial, onSubmit, onCancel, submitting }) {
  const [formData, setFormData] = useState(initial || EMPTY_FORM);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const firstFieldRef = useRef(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const set = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const handlePincodeChange = async (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    set('pincode', val);
    if (val.length === 6) {
      setPincodeLoading(true);
      const result = await lookupPincode(val);
      if (result) {
        const stateMatch = INDIAN_STATES.find(
          s => s.name.toLowerCase() === result.state.toLowerCase()
        );
        setFormData(prev => ({
          ...prev,
          pincode: val,
          city: result.city || prev.city,
          state: stateMatch?.code || prev.state,
        }));
      }
      setPincodeLoading(false);
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    const cleanPhone = formData.phone.replace(/\s/g, '');
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      errors.phone = 'Enter a valid 10-digit mobile number starting with 6–9';
    }
    if (!formData.line1.trim()) errors.line1 = 'Address line 1 is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.state) errors.state = 'State is required';
    if (!/^\d{6}$/.test(formData.pincode)) {
      errors.pincode = 'Enter a valid 6-digit pincode';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ ...formData, country: 'IN' });
  };

  return (
    <form className="account-address-form" onSubmit={handleSubmit} noValidate>
      <h3 className="account-address-form__title">
        {initial?._id ? 'Edit Address' : 'Add New Address'}
      </h3>

      {/* Label */}
      <div className="account-address-form__group">
        <label className="account-address-form__label">Address Label</label>
        <div className="account-address-form__radio-group">
          {['Home', 'Work', 'Other'].map(opt => (
            <label key={opt} className="account-address-form__radio">
              <input
                type="radio"
                name="label"
                value={opt}
                checked={formData.label === opt}
                onChange={handleChange}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Full Name */}
      <div className={`account-address-form__field ${fieldErrors.fullName ? 'account-address-form__field--error' : ''}`}>
        <label htmlFor="fullName" className="account-address-form__label">
          Full Name *
        </label>
        <input
          ref={firstFieldRef}
          id="fullName"
          name="fullName"
          type="text"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="John Doe"
          className="account-form-input"
          aria-invalid={!!fieldErrors.fullName}
        />
        {fieldErrors.fullName && (
          <span className="account-form-error" role="alert">{fieldErrors.fullName}</span>
        )}
      </div>

      {/* Phone */}
      <div className={`account-address-form__field ${fieldErrors.phone ? 'account-address-form__field--error' : ''}`}>
        <label htmlFor="phone" className="account-address-form__label">
          Mobile Number *
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ padding: '0 12px', color: '#666' }}>+91</span>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="9876543210"
            maxLength={10}
            className="account-form-input"
            style={{ flex: 1 }}
            aria-invalid={!!fieldErrors.phone}
          />
        </div>
        {fieldErrors.phone && (
          <span className="account-form-error" role="alert">{fieldErrors.phone}</span>
        )}
      </div>

      {/* Line 1 */}
      <div className={`account-address-form__field ${fieldErrors.line1 ? 'account-address-form__field--error' : ''}`}>
        <label htmlFor="line1" className="account-address-form__label">
          Address Line 1 *
        </label>
        <input
          id="line1"
          name="line1"
          type="text"
          value={formData.line1}
          onChange={handleChange}
          placeholder="Flat/House No., Building Name"
          className="account-form-input"
          aria-invalid={!!fieldErrors.line1}
        />
        {fieldErrors.line1 && (
          <span className="account-form-error" role="alert">{fieldErrors.line1}</span>
        )}
      </div>

      {/* Line 2 */}
      <div className="account-address-form__field">
        <label htmlFor="line2" className="account-address-form__label">
          Address Line 2 <span style={{ fontWeight: 'normal', color: '#999' }}>(Optional)</span>
        </label>
        <input
          id="line2"
          name="line2"
          type="text"
          value={formData.line2}
          onChange={handleChange}
          placeholder="Street, Area, Landmark"
          className="account-form-input"
        />
      </div>

      {/* Pincode */}
      <div className={`account-address-form__field ${fieldErrors.pincode ? 'account-address-form__field--error' : ''}`}>
        <label htmlFor="pincode" className="account-address-form__label">
          Pincode *
        </label>
        <input
          id="pincode"
          name="pincode"
          type="text"
          value={formData.pincode}
          onChange={handlePincodeChange}
          placeholder="400001"
          maxLength={6}
          className="account-form-input"
          aria-invalid={!!fieldErrors.pincode}
        />
        {pincodeLoading && <span style={{ fontSize: '12px', color: '#666' }}>Looking up...</span>}
        {fieldErrors.pincode && (
          <span className="account-form-error" role="alert">{fieldErrors.pincode}</span>
        )}
      </div>

      {/* City and State */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className={`account-address-form__field ${fieldErrors.city ? 'account-address-form__field--error' : ''}`}>
          <label htmlFor="city" className="account-address-form__label">
            City *
          </label>
          <input
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleChange}
            placeholder="Mumbai"
            className="account-form-input"
            aria-invalid={!!fieldErrors.city}
          />
          {fieldErrors.city && (
            <span className="account-form-error" role="alert">{fieldErrors.city}</span>
          )}
        </div>
        <div className={`account-address-form__field ${fieldErrors.state ? 'account-address-form__field--error' : ''}`}>
          <label htmlFor="state" className="account-address-form__label">
            State *
          </label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            className="account-form-input"
            aria-invalid={!!fieldErrors.state}
          >
            <option value="">Select state</option>
            {INDIAN_STATES.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
          {fieldErrors.state && (
            <span className="account-form-error" role="alert">{fieldErrors.state}</span>
          )}
        </div>
      </div>

      {/* Default checkbox */}
      <label className="account-address-form__checkbox">
        <input
          type="checkbox"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
        />
        <span>Set as default address</span>
      </label>

      {/* Actions */}
      <div className="account-address-form__actions">
        <button
          type="button"
          className="account-form-submit"
          onClick={onCancel}
          style={{ backgroundColor: '#f5f5f5', color: '#333' }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="account-form-submit"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : initial?._id ? 'Save Changes' : 'Add Address'}
        </button>
      </div>
    </form>
  );
}

function AccountAddresses() {
  const navigate = useNavigate();
  const { token, isAuthenticated, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState(null); // null | 'add' | address object (edit)
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const pageParam = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const [page, setPage] = useState(pageParam);
  const PER_PAGE = 6;

  const fetchAddresses = useCallback(async (attempt = 0) => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Allow for backend cold start

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/addresses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (controller.signal.aborted) return;

      // Handle 401 - session expired
      if (response.status === 401) {
        logout();
        navigate('/login', {
          replace: true,
          state: { reason: 'SESSION_EXPIRED' },
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (controller.signal.aborted) return;

      if (data.success) {
        // Backend uses "data" for payload in standardized responses
        // but support legacy "addresses" field just in case.
        const raw = data.data ?? data.addresses ?? [];
        const list = Array.isArray(raw) ? raw.filter(a => a && a._id) : [];
        setAddresses(list);
        setError(null);
      } else {
        setAddresses([]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (controller.signal.aborted) {
        setError('Request timed out. Please check your connection and try again.');
      } else {
        // Retry logic: up to 3 attempts with exponential backoff
        const maxRetries = 3;
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.warn(`[AccountAddresses] Fetch failed, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          
          setTimeout(() => {
            if (!cancelled) {
              fetchAddresses(attempt + 1);
            }
          }, delay);
          return;
        }

        // All retries failed
        setError('Failed to load addresses. Please refresh the page.');
        if (process.env.NODE_ENV === 'development') {
          console.error('[AccountAddresses] fetch error:', err);
        }
        setAddresses([]);
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [token, isAuthenticated, navigate, logout, API_URL]);

  // Keep local page in sync when query param changes externally
  useEffect(() => {
    setPage(pageParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam]);

  useEffect(() => {
    const cleanup = fetchAddresses(0);
    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchAddresses]);

  const handleEdit = (address) => {
    setFormMode(address);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    setDeleting(addressId);
    setMessage({ type: '', text: '' });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${API_URL}/addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        setAddresses(prev => prev.filter(a => a._id !== addressId));
        setMessage({ type: 'success', text: 'Address deleted successfully' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to delete address' });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setMessage({ type: 'error', text: 'Request timed out. Please try again.' });
      } else {
        console.error('Error deleting address:', error);
        setMessage({ type: 'error', text: 'Network error. Please try again.' });
      }
    } finally {
      setDeleting(null);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${API_URL}/addresses/${addressId}/default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        await fetchAddresses(); // Refresh addresses
        setMessage({ type: 'success', text: 'Default address updated' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to set default address' });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setMessage({ type: 'error', text: 'Request timed out. Please try again.' });
      } else {
        console.error('Error setting default address:', error);
        setMessage({ type: 'error', text: 'Network error. Please try again.' });
      }
    }
  };

  const handleSubmitForm = async (formData) => {
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const isEdit = !!formData._id;
    const url = isEdit ? `${API_URL}/addresses/${formData._id}` : `${API_URL}/addresses`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Prepare data for API (convert phone to clean format)
      const cleanPhone = formData.phone.replace(/\s/g, '');
      const payload = {
        label: formData.label,
        fullName: formData.fullName.trim(),
        phone: cleanPhone,
        line1: formData.line1.trim(),
        line2: formData.line2?.trim() || undefined,
        city: formData.city.trim(),
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country || 'IN',
        isDefault: formData.isDefault || false,
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        await fetchAddresses(); // Refresh addresses
        setFormMode(null);
        setMessage({ 
          type: 'success', 
          text: isEdit ? 'Address updated successfully' : 'Address added successfully' 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const errorData = await response.json();
        setMessage({ 
          type: 'error', 
          text: errorData.message || (isEdit ? 'Failed to update address' : 'Failed to add address') 
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setMessage({ type: 'error', text: 'Request timed out. Please try again.' });
      } else {
        console.error('Error saving address:', error);
        setMessage({ type: 'error', text: 'Network error. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelForm = () => {
    setFormMode(null);
    setMessage({ type: '', text: '' });
  };

  const total = addresses.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PER_PAGE;
  const endIndex = startIndex + PER_PAGE;
  const pageItems = addresses.slice(startIndex, endIndex);

  const handlePageChange = (nextPage) => {
    const target = Math.max(1, Math.min(nextPage, totalPages));
    setPage(target);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (target === 1) {
        next.delete('page');
      } else {
        next.set('page', String(target));
      }
      return next;
    });
  };

  return (
    <>
      <Helmet>
        <title>My Addresses | {BRAND_NAME}</title>
        <meta name="description" content="Manage your delivery addresses for faster checkout." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account/addresses`} />
      </Helmet>
      
      <Layout showFooter={true} showBottomTab={true}>
        <div className="account-page">
          <div className="account-container">
            <div className="account-header">
              <Link to="/account" className="account-back">← Back to Account</Link>
              <h1 className="account-title">My Addresses</h1>
              {total > 0 ? (
                <p className="account-subtitle">
                  {total} address{total !== 1 ? 'es' : ''} · Page {currentPage} of {totalPages}
                </p>
              ) : (
                <p className="account-subtitle">Manage your delivery addresses.</p>
              )}
            </div>

            {message.text && (
              <div className={`account-message ${message.type === 'success' ? 'account-message--success' : 'account-message--error'}`}>
                {message.text}
              </div>
            )}

            <div className="account-addresses">
              {error && !loading ? (
                <div className="account-error" role="alert">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="account-error__icon" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <p className="account-error__message">{error}</p>
                  <button
                    onClick={() => fetchAddresses(0)}
                    className="account-error__retry btn-primary"
                    aria-label="Retry loading addresses"
                  >
                    Retry
                  </button>
                </div>
              ) : loading ? (
                <div className="account-loading">
                  <div className="account-loading__skeleton">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="account-loading__skeleton-item">
                        <div className="account-loading__skeleton-line shimmer" />
                        <div className="account-loading__skeleton-line shimmer" />
                        <div className="account-loading__skeleton-line shimmer" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : formMode ? (
                <AddressForm
                  initial={formMode === 'add' ? null : formMode}
                  onSubmit={handleSubmitForm}
                  onCancel={handleCancelForm}
                  submitting={submitting}
                />
              ) : (
                <>
                  {addresses.length === 0 ? (
                    <div className="account-empty">
                      <p>You haven't added any addresses yet.</p>
                      <button
                        className="account-address-add"
                        onClick={() => setFormMode('add')}
                      >
                        <span>Add Your First Address</span>
                      </button>
                    </div>
                  ) : (
                    <>
                      {pageItems.map((address) => (
                        <div key={address._id} className="account-address-card">
                          {address.isDefault && (
                            <span className="account-address__badge">Default</span>
                          )}
                          <div className="account-address__content">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <h3 className="account-address__name">{address.fullName}</h3>
                              <span className="account-address__label" style={{ 
                                fontSize: '12px', 
                                color: '#666', 
                                padding: '2px 8px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '4px' 
                              }}>
                                {address.label}
                              </span>
                            </div>
                            <p className="account-address__line">{address.line1}</p>
                            {address.line2 && (
                              <p className="account-address__line">{address.line2}</p>
                            )}
                            <p className="account-address__line">
                              {address.city}, {address.state} {address.pincode}
                            </p>
                            <p className="account-address__mobile">Mobile: {address.phone}</p>
                          </div>
                          <div className="account-address__actions">
                            <button
                              className="account-address-action"
                              onClick={() => handleEdit(address)}
                              disabled={deleting === address._id}
                            >
                              Edit
                            </button>
                            {!address.isDefault && (
                              <button
                                className="account-address-action"
                                onClick={() => handleSetDefault(address._id)}
                                disabled={deleting === address._id}
                                style={{ fontSize: '12px', padding: '6px 12px' }}
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              className="account-address-action account-address-action--danger"
                              onClick={() => handleDelete(address._id)}
                              disabled={deleting === address._id}
                            >
                              {deleting === address._id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        className="account-address-add"
                        onClick={() => setFormMode('add')}
                      >
                        <span>Add New Address</span>
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            {!loading && !error && totalPages > 1 && !formMode && (
              <nav className="account-pagination" aria-label="Address pages">
                <button
                  type="button"
                  className="account-pagination__btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </button>
                <span className="account-pagination__info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="account-pagination__btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default AccountAddresses;
